import React, { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

// Initialize Mapbox with access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

const BaseMap = ({ 
  className = '', 
  center = [-98.5795, 39.8283], 
  zoom = 3,
  onMapReady,
  onClick,
  children 
}) => {
  const mapContainer = useRef(null)
  const map = useRef(null)
  const [isMapInitialized, setIsMapInitialized] = useState(false)

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: center,
        zoom: zoom,
        touchZoomRotate: true,
        touchPitch: false,
        dragRotate: false,
      })

      // Add zoom controls (compact on mobile)
      map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')

      // Add geolocation control for mobile users
      map.current.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          trackUserLocation: false,
          showUserHeading: false,
        }),
        'top-right'
      )

      // Add error handling
      map.current.on('error', (e) => {
        console.warn('Mapbox error (non-critical):', e.error)
      })

      map.current.on('load', () => {
        setIsMapInitialized(true)
        if (onMapReady) {
          onMapReady(map.current)
        }
      })

      // Add click handler if provided
      if (onClick) {
        map.current.on('click', onClick)
      }

    } catch (error) {
      console.error('Error initializing map:', error)
    }

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, []) // Only initialize once

  // Update click handler when it changes
  useEffect(() => {
    if (!map.current) return

    // Remove existing click handler
    map.current.off('click')

    // Add new click handler if provided
    if (onClick) {
      map.current.on('click', onClick)
    }
  }, [onClick])

  // Update map center when center prop changes
  useEffect(() => {
    if (!map.current || !isMapInitialized) return

    console.log('BaseMap center prop changed, flying to:', center, 'zoom:', zoom)
    map.current.flyTo({
      center: center,
      zoom: zoom
    })
  }, [center, zoom, isMapInitialized])

  return (
    <div className={`h-full ${className}`}>
      <div
        ref={mapContainer}
        className="h-full min-h-[400px] w-full rounded-lg border border-border"
      />
      {isMapInitialized && children && 
        React.Children.map(children, child => 
          React.cloneElement(child, { map: map.current })
        )
      }
    </div>
  )
}

export default BaseMap